export type PublicEnvKey =
  | "APP_VERSION"
  | "CLOUDFLARE_TURNSTILE_SITE_KEY"
  | "CONVEX_API_URL"
  | "CONVEX_URL"
  | "DATADOG_APPLICATION_ID"
  | "DATADOG_CLIENT_TOKEN"
  | "DATADOG_ENV"
  | "DATADOG_SITE"
  | "GTM_ID"
  | "GTM_SERVER_URL"
  | "INTERCOM_APP_ID"
  | "STATSIG_CLIENT_KEY";

export type Bundler = "vite" | "next";

type Readers = Record<PublicEnvKey, () => string | undefined>;

// Every read stays a literal member expression: Vite inlines only
// `import.meta.env.VITE_*` and Next only `process.env.NEXT_PUBLIC_*`, so a
// computed key yields undefined in the browser bundle. The getters keep the
// other bundler's expression from ever evaluating: `process` does not exist
// in a Vite browser bundle, and `import.meta.env` does not exist under Next.
const READERS: Record<Bundler, Readers> = {
  vite: {
    APP_VERSION: () => import.meta.env?.VITE_APP_VERSION,
    CLOUDFLARE_TURNSTILE_SITE_KEY: () =>
      import.meta.env?.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY,
    CONVEX_API_URL: () => import.meta.env?.VITE_CONVEX_API_URL,
    CONVEX_URL: () => import.meta.env?.VITE_CONVEX_URL,
    DATADOG_APPLICATION_ID: () => import.meta.env?.VITE_DATADOG_APPLICATION_ID,
    DATADOG_CLIENT_TOKEN: () => import.meta.env?.VITE_DATADOG_CLIENT_TOKEN,
    DATADOG_ENV: () => import.meta.env?.VITE_DATADOG_ENV,
    DATADOG_SITE: () => import.meta.env?.VITE_DATADOG_SITE,
    GTM_ID: () => import.meta.env?.VITE_GTM_ID,
    GTM_SERVER_URL: () => import.meta.env?.VITE_GTM_SERVER_URL,
    INTERCOM_APP_ID: () => import.meta.env?.VITE_INTERCOM_APP_ID,
    STATSIG_CLIENT_KEY: () => import.meta.env?.VITE_STATSIG_CLIENT_KEY,
  },
  next: {
    APP_VERSION: () => process.env.NEXT_PUBLIC_APP_VERSION,
    CLOUDFLARE_TURNSTILE_SITE_KEY: () =>
      process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY,
    CONVEX_API_URL: () => process.env.NEXT_PUBLIC_CONVEX_API_URL,
    CONVEX_URL: () => process.env.NEXT_PUBLIC_CONVEX_URL,
    DATADOG_APPLICATION_ID: () =>
      process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
    DATADOG_CLIENT_TOKEN: () => process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
    DATADOG_ENV: () => process.env.NEXT_PUBLIC_DATADOG_ENV,
    DATADOG_SITE: () => process.env.NEXT_PUBLIC_DATADOG_SITE,
    GTM_ID: () => process.env.NEXT_PUBLIC_GTM_ID,
    GTM_SERVER_URL: () => process.env.NEXT_PUBLIC_GTM_SERVER_URL,
    INTERCOM_APP_ID: () => process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
    STATSIG_CLIENT_KEY: () => process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY,
  },
};

// Vite always defines `MODE`; Next leaves `import.meta.env` undefined.
// Convex throws a TypeError on `import.meta` at module-load time — the
// try-catch falls back to "next" so the vite readers are never called there.
const BUNDLER: Bundler = (() => {
  try {
    return import.meta.env?.MODE == null ? "next" : "vite";
  } catch {
    return "next";
  }
})();

/** A public build-time value: `VITE_<KEY>` under Vite, else `NEXT_PUBLIC_<KEY>`. */
export function publicEnv(
  key: PublicEnvKey,
  bundler: Bundler = BUNDLER,
): string | undefined {
  return READERS[bundler][key]();
}
